// How AI went for a document that arrived by email, and how we say it: the same words in the
// reply email (worker/inbox/mail.ts) and in the app's Inbox.
import { formatDate } from "./dates";

export interface AiNote {
  /**
   *   ai: AI filled the form · credits: not enough AI credits, rules only · paused: the daily
   *   AI ceiling for everyone was reached · failed: the model failed · off: AI isn't set up ·
   *   no_text: no text found on the server (a scan), so the app reads it.
   */
  status: "ai" | "credits" | "paused" | "failed" | "off" | "no_text";
  charged?: number;
  /** Grace credits we covered for this read. */
  grace?: number;
  /** A full scan was asked for, but a quick scan ran: what the full scan needed and what was left. */
  downgraded?: { needs: number; had: number } | null;
  /** A quick scan read only part of a long document. */
  partial?: boolean;
  /** When credits ran out: when they refill, and whether more can be bought. */
  refillsOn?: string;
  canBuy?: boolean;
}

export interface NoteLine {
  text: string;
  /** Show a "Buy credits" link after it. */
  buy?: boolean;
  tone?: "gift";
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "🎁 You were 3 credits short, so this one's on us." */
export const graceLine = (credits: number) => `🎁 You were ${plural(credits, "credit")} short, so this one's on us.`;

/** "A full scan needed up to 420 credits and you had 150 left, so AI read the main parts instead (a quick scan)." */
export const downgradeLine = (d: { needs: number; had: number }) =>
  `A full scan needed up to ${d.needs} credits and you had ${d.had} left, so AI read the main parts instead (a quick scan). You can run a full scan when your credits refresh.`;

export function aiNoteLines(note: AiNote | null, where: "email" | "app"): NoteLine[] {
  if (!note) return [];
  const out: NoteLine[] = [];
  const open = where === "email" ? "Open it in the app" : "Open the file";
  switch (note.status) {
    case "ai":
      out.push({ text: `Filled in by AI${note.charged ? ` (${plural(note.charged, "credit")})` : ""}. Please check the details: AI can be wrong.` });
      if (note.grace) out.push({ text: graceLine(note.grace), tone: "gift" });
      if (note.downgraded) out.push({ text: downgradeLine(note.downgraded) });
      else if (note.partial) out.push({ text: "It's a long document, so AI read the first pages and the most relevant parts. You can run a full scan from the app." });
      break;
    case "credits":
      out.push({ text: `Your AI credits are used up for now, so we filled in what we could without AI. They refill on ${note.refillsOn ? formatDate(note.refillsOn, "en-IN") : "soon"}.` });
      out.push(note.canBuy ? { text: "Need more now?", buy: true } : { text: "Buying more credits is coming soon." });
      break;
    case "paused":
      out.push({ text: "AI reading is busy today, so we filled in what we could without AI. Your credits weren't used." });
      break;
    case "failed":
      out.push({ text: "AI couldn't read it right now, so we filled in what we could without AI." });
      break;
    case "off":
      out.push({ text: `${open} to read it and fill in the details.` });
      break;
    case "no_text":
      out.push({ text: `This looks like a scan, so we couldn't read it on our server. ${open}: scans are read on your device.` });
      break;
  }
  return out;
}
